import type { Table } from "@tanstack/react-table";
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronsLeftIcon,
	ChevronsRightIcon,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

interface DataTablePaginationProps<TData> {
	table: Table<TData>;
	pageSizeOptions?: number[];
	canPreviousPage: boolean;
	canNextPage: boolean;
}

export function DataTablePagination<TData>({
	table,
	pageSizeOptions = [10, 20, 30, 40, 50],
	canPreviousPage,
	canNextPage,
}: DataTablePaginationProps<TData>) {

	const currentPage = table.getState().pagination.pageIndex + 1;
	const totalPages = table.getPageCount();

	// Debug: Log pagination state changes
	React.useEffect(() => {
		// Este log ayuda a verificar si las props visuales coinciden con el estado interno
		console.log('[PaginationComponent] Render State:', {
			currentPage,
			totalPages,
			pageSize: table.getState().pagination.pageSize,
			canPrev: canPreviousPage,
			canNext: canNextPage
		});
	}, [currentPage, totalPages, canPreviousPage, canNextPage, table]);

	const handlePreviousPage = React.useCallback(() => {
		console.log('[PaginationComponent] Click: Previous Page');
		table.previousPage();
	}, [table]);

	const handleNextPage = React.useCallback(() => {
		console.log('[PaginationComponent] Click: Next Page');
		table.nextPage();
	}, [table]);

	const handleFirstPage = React.useCallback(() => {
		console.log('[PaginationComponent] Click: First Page');
		table.setPageIndex(0);
	}, [table]);

	const handleLastPage = React.useCallback(() => {
		console.log('[PaginationComponent] Click: Last Page ->', totalPages - 1);
		table.setPageIndex(totalPages - 1);
	}, [table, totalPages]);

	const handlePageSizeChange = React.useCallback((value: string) => {
		console.log('[PaginationComponent] Change: Page Size ->', value);
		table.setPageSize(Number(value));
	}, [table]);

	return (
		<div className="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8">
			<div className="flex-1 whitespace-nowrap text-muted-foreground text-sm">
				{table.getFilteredSelectedRowModel().rows.length > 0 && (
					<span>
						{table.getFilteredSelectedRowModel().rows.length} de{" "}
						{table.getFilteredRowModel().rows.length} fila(s) seleccionada(s)
					</span>
				)}
			</div>
			<div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
				<div className="flex items-center space-x-2">
					<p className="whitespace-nowrap font-medium text-sm">
						Filas por página
					</p>
					<Select
						value={`${table.getState().pagination.pageSize}`}
						onValueChange={handlePageSizeChange}
					>
						<SelectTrigger className="h-8 w-[4.5rem]">
							<SelectValue placeholder={table.getState().pagination.pageSize} />
						</SelectTrigger>
						<SelectContent side="top">
							{pageSizeOptions.map((pageSize) => (
								<SelectItem key={pageSize} value={`${pageSize}`}>
									{pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center justify-center font-medium text-sm">
					Página {currentPage} de {totalPages}
				</div>
				<div className="flex items-center space-x-2">
					<Button
						aria-label="Ir a la primera página"
						variant="outline"
						className="hidden size-8 p-0 lg:flex"
						onClick={handleFirstPage}
						disabled={!canPreviousPage}
					>
						<ChevronsLeftIcon className="size-4" aria-hidden="true" />
					</Button>
					<Button
						aria-label="Ir a la página anterior"
						variant="outline"
						size="icon"
						className="size-8"
						onClick={handlePreviousPage}
						disabled={!canPreviousPage}
					>
						<ChevronLeftIcon className="size-4" aria-hidden="true" />
					</Button>
					<Button
						aria-label="Ir a la página siguiente"
						variant="outline"
						size="icon"
						className="size-8"
						onClick={handleNextPage}
						disabled={!canNextPage}
					>
						<ChevronRightIcon className="size-4" aria-hidden="true" />
					</Button>
					<Button
						aria-label="Ir a la última página"
						variant="outline"
						size="icon"
						className="hidden size-8 lg:flex"
						onClick={handleLastPage}
						disabled={!canNextPage}
					>
						<ChevronsRightIcon className="size-4" aria-hidden="true" />
					</Button>
				</div>
			</div>
		</div>
	);
}